import type { DispatchOnFull, DispatchPreference, Pool } from "@/api/types";

/**
 * Controlled editor for a service's dispatch mode — the two orthogonal
 * axes the controller exposes on the Service row:
 *
 *   preference (axis 1): who serves a request — `sticky` (same caller
 *   returns to the same pod; Maglev + hash policies + the pool-priority
 *   failover cascade) or `fill` (the pod with the largest free-slot
 *   share; weighted least-request over each pool's
 *   `capacity_per_replica`).
 *
 *   on_full (axis 2): what happens when the first-choice pod is
 *   saturated — `queue` (wait in the pod's own queue), `spill` (while a
 *   pod is above the spill factor % of cluster-average load, hash picks
 *   overflow to the next host; sticky-only), or `reject` (the pod
 *   answers 429 and Envoy transparently retries another host).
 *
 * The component is pure ({value, onChange, disabled}); the parent owns
 * persistence, mirroring SLOEditor. The one cross-field rule — fill
 * never combines with spill (the mechanism lives inside Envoy's hash
 * LBs) — is enforced here by disabling the spill segment under fill and
 * snapping on_full back to queue if the user switches to fill while
 * spill is selected, so the parent never has to send an invalid pair.
 */

export interface DispatchValue {
  preference: DispatchPreference;
  on_full: DispatchOnFull;
  spill_factor: number;
}

interface Props {
  value: DispatchValue;
  onChange: (next: DispatchValue) => void;
  disabled?: boolean;
  pools: Pool[];
  hashPolicyCount: number;
}

const PREFERENCE_INFO: Record<DispatchPreference, string> = {
  sticky:
    "Same caller, same pod.\n" +
    "Consistent hashing (Maglev) over the service's hash policies keeps a " +
    "key (user / conversation / cache id) on one pod so KV caches and " +
    "session state stay warm. Pool priority doubles as the traffic " +
    "failover order (P0 first, spill down when it is starved).",
  fill: "Any pod is fine — send work where there is room.\n" +
    "Weighted least-request: each pod's weight is its slot count " +
    "(capacity_per_replica), so Envoy picks the pod with the largest " +
    "free-slot share per request. All pools serve at once in proportion " +
    "to their slots; pool priority only orders provisioning.",
};

const ON_FULL_INFO: Record<DispatchOnFull, string> = {
  queue:
    "Keep sending; excess waits inside the pod's own queue.\n" +
    "No cap — choose this when the workload queues gracefully.",
  spill:
    "While a pod is busier than the spill factor (% of the cluster " +
    "average), requests hashed to it temporarily go to the next host, " +
    "and return once it cools down. Costs one cache miss per spilled " +
    "request. Sticky only — fill already avoids hot pods per request.",
  reject:
    "A full pod answers 429 and backstage retries another pod " +
    "automatically — clients only ever see a 429 when every pod is full.\n" +
    "Hard cap — the 429 contract is the only workload-side work; " +
    "clients have nothing to implement.",
};

/** Documentation shorthand for the (preference, on_full) pair. */
export function dispatchModeName(v: DispatchValue): string {
  if (v.preference === "sticky") {
    if (v.on_full === "spill") return "AFFINITY-SPILL";
    if (v.on_full === "reject") return "AFFINITY-REJECT";
    return "AFFINITY";
  }
  if (v.on_full === "reject") return "CAPACITY-REJECT";
  return "CAPACITY";
}

/** One plain-language sentence describing what the current pair does. */
function behaviourSummary(v: DispatchValue): string {
  const pick =
    v.preference === "sticky"
      ? "Each key (hash policy) keeps returning to the same pod"
      : "Each request goes to the pod with the largest free-slot share";
  const full =
    v.on_full === "queue"
      ? "a busy pod queues the excess internally"
      : v.on_full === "spill"
        ? `a pod above ${v.spill_factor}% of the cluster-average load hands its hash picks to the next host until it cools down`
        : "a full pod rejects with 429 and Envoy retries another host transparently";
  return `${pick}; ${full}.`;
}

function Segments<T extends string>({
  options,
  value,
  onSelect,
  info,
  disabled,
  disabledOptions,
  labels,
}: {
  options: readonly T[];
  value: T;
  onSelect: (v: T) => void;
  info: Record<T, string>;
  disabled?: boolean;
  disabledOptions?: Partial<Record<T, string>>;
  labels?: Partial<Record<T, string>>;
}) {
  return (
    <div className="surge-segments" role="radiogroup">
      {options.map((opt) => {
        const blockedReason = disabledOptions?.[opt];
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={value === opt}
            className={`surge-segments__btn${value === opt ? " is-active" : ""}`}
            title={blockedReason ?? info[opt]}
            disabled={disabled || blockedReason != null}
            style={blockedReason != null ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
            onClick={() => onSelect(opt)}
          >
            {labels?.[opt] ?? opt}
          </button>
        );
      })}
    </div>
  );
}

export function DispatchEditor({
  value,
  onChange,
  disabled,
  pools,
  hashPolicyCount,
}: Props) {
  const setPreference = (preference: DispatchPreference) => {
    // fill x spill is invalid (the API refuses it): switching to fill
    // while spill is selected snaps on_full back to queue rather than
    // leaving the form in an unsaveable state.
    const on_full =
      preference === "fill" && value.on_full === "spill"
        ? "queue"
        : value.on_full;
    onChange({ ...value, preference, on_full });
  };

  const slotRows = pools
    .slice()
    .sort((a, b) => a.priority - b.priority)
    .map((p) => ({
      name: p.display_name ?? p.sky_service_name,
      slots: p.capacity_per_replica ?? 1,
    }));
  const slotsAllDefault = slotRows.every((r) => r.slots === 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="form-grid">
        <label htmlFor="dispatch-preference">Preference</label>
        <div className="field" id="dispatch-preference">
          <Segments
            options={["sticky", "fill"] as const}
            labels={{ sticky: "sticky — same pod", fill: "fill — free slots" }}
            value={value.preference}
            onSelect={setPreference}
            info={PREFERENCE_INFO}
            disabled={disabled}
          />
          <div className="field-hint">
            {value.preference === "sticky"
              ? "Same caller returns to the same pod (Maglev + hash policies); pool priority is the failover order."
              : "Requests fill pods in proportion to free slots (capacity_per_replica); every pool serves at once."}
          </div>
        </div>

        <label htmlFor="dispatch-on-full">When full</label>
        <div className="field" id="dispatch-on-full">
          <Segments
            options={["queue", "spill", "reject"] as const}
            labels={{
              queue: "queue — wait",
              spill: "spill — next host",
              reject: "reject — 429 + retry",
            }}
            value={value.on_full}
            onSelect={(on_full) => onChange({ ...value, on_full })}
            info={ON_FULL_INFO}
            disabled={disabled}
            disabledOptions={
              value.preference === "fill"
                ? {
                    spill:
                      "spill needs sticky dispatch: it lives inside Envoy's hash LBs, " +
                      "and fill already avoids hot pods per request. Use reject for a hard cap.",
                  }
                : undefined
            }
          />
          <div className="field-hint">{ON_FULL_INFO[value.on_full].split("\n")[0]}</div>
        </div>

        {value.on_full === "spill" && (
          <>
            <label htmlFor="dispatch-spill-factor">Spill factor</label>
            <div className="field">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  id="dispatch-spill-factor"
                  type="number"
                  min={100}
                  max={1000}
                  step={5}
                  style={{ width: 110 }}
                  value={value.spill_factor}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      spill_factor: Number(e.target.value),
                    })
                  }
                />
                <span className="muted">% of cluster-average load</span>
              </div>
              <div className="field-hint">
                150 = a pod may run 1.5× the average before its hash picks
                overflow to the next host. 100–1000; lower spills sooner.
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flash flash--info" style={{ margin: 0 }}>
        {behaviourSummary(value)}
      </div>

      {value.preference === "fill" && (
        <div className="field-hint" style={{ marginTop: -8 }}>
          per-pod slots ={" "}
          {slotRows.length ? (
            slotRows.map((r, i) => (
              <span key={r.name}>
                {i > 0 && ", "}
                <span className="mono">
                  {r.name} ×{r.slots}
                </span>
              </span>
            ))
          ) : (
            <span className="mono">no pools yet</span>
          )}{" "}
          — edit each pool's <span className="mono">capacity_per_replica</span>.
          {slotsAllDefault && slotRows.length > 0 && (
            <>
              {" "}
              All pools are at the default 1 slot, so fill currently spreads
              evenly; set real slot counts (e.g. A100 ×5, H100 ×20) to weight
              by capacity.
            </>
          )}
        </div>
      )}

      {value.preference === "fill" && hashPolicyCount > 0 && (
        <div className="flash flash--warn" style={{ margin: 0 }}>
          this service has {hashPolicyCount} hash{" "}
          {hashPolicyCount === 1 ? "policy" : "policies"} — they are ignored
          under fill dispatch (no affinity). Switch back to sticky to restore
          key affinity.
        </div>
      )}

      {value.preference === "sticky" && hashPolicyCount === 0 && (
        <div className="flash flash--warn" style={{ margin: 0 }}>
          sticky without hash policies degrades to a random spread — there is
          no key to stick on, and load is not considered either. Add a hash
          policy, or consider fill dispatch.
        </div>
      )}

      {value.on_full === "reject" && (
        <div className="flash flash--warn" style={{ margin: 0 }}>
          <strong>Who does what under reject</strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: "1.2em" }}>
            <li>
              <strong>Your workload</strong> must answer{" "}
              <span className="mono">429</span> the moment its slots are full
              (not 503 — outlier detection would eject the pod). This is the
              only thing you have to implement.
            </li>
            <li>
              <strong>backstage retries automatically</strong> — Envoy absorbs
              the 429 and re-dispatches to up to 3 pods it hasn't tried.
              While any pod has a free slot, callers never see a 429.
            </li>
            <li>
              <strong>Clients</strong> receive a 429 only when{" "}
              <em>every</em> pod is full. That final 429 is genuine capacity
              backpressure, not a transient error — clients should slow down
              or queue, not hot-retry.
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
